import React, { useState, useRef, useEffect, useMemo } from 'react';
import styles from './autocomplete.module.scss';
import { AIFieldType, CityOption } from '@/types/heroBlock.types';
import { createWorldCitiesOptions } from '@/utils/createWorldCitiesOptions';

interface AutocompleteProps extends AIFieldType {
  icon?: string;
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  className = '',
  property1 = 'Default',
  placeholder,
  label,
  value = '',
  onChange,
  icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<CityOption[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const cityOptions = useMemo(() => createWorldCitiesOptions(), []);

  const filterAndSort = (inputValue: string): CityOption[] => {
    const query = inputValue.toLowerCase().trim();

    const filtered = cityOptions.filter(
      (option) =>
        option.title.toLowerCase().includes(query) ||
        option.airports?.some(
          (airport) =>
            airport.title.toLowerCase().includes(query) ||
            airport.code?.toLowerCase() === query
        )
    );

    const score = (option: CityOption): number => {
      const titleLower = option.title.toLowerCase();
      const isUS = option.countryCode?.toUpperCase() === 'US';
      const hasExactCodeMatch = option.airports?.some(
        (a) => a.code?.toLowerCase() === query
      );
      const hasExactTitleMatch = titleLower === query;
      const isExact = hasExactCodeMatch || hasExactTitleMatch;

      // 1. Exact match + US
      if (isExact && isUS) return 0;
      // 2. Any match + US
      if (isUS) return 1;
      // 3. Exact match, not US
      if (isExact) return 2;
      // 4. Everything else
      return 3;
    };

    filtered.sort((a, b) => score(a) - score(b));
    return filtered.slice(0, 10);
  };

  const handleInputChange = (inputValue: string) => {
    onChange?.(inputValue);

    if (inputValue.length > 0) {
      setFilteredOptions(filterAndSort(inputValue));
      setIsOpen(true);
    } else {
      setFilteredOptions([]);
      setIsOpen(false);
    }
  };

  const handleOptionSelect = (
    option: CityOption,
    airport?: { id: string; title: string; code?: string }
  ) => {
    onChange?.(airport ? airport.title : option.title);
    setIsOpen(false);
    setFilteredOptions([]);
  };

  const handleInputFocus = () => {
    if (value && value.length > 0) {
      setFilteredOptions(filterAndSort(value));
      setIsOpen(true);
    }
  };

  const handleInputBlur = (e: React.FocusEvent) => {
    setTimeout(() => {
      if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
        setIsOpen(false);
      }
    }, 150);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={[styles.root, className].join(' ')} ref={dropdownRef}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles['input-container']} data-property1={property1}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        {icon && <img src={icon} alt="" className={styles.inputIcon} />}
        <input
          className={[styles.input, icon ? styles.withIcon : ''].join(' ')}
          placeholder={placeholder}
          type="text"
          disabled={property1 === 'Disabled'}
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
        />
        {isOpen && filteredOptions.length > 0 && (
          <div className={styles.dropdown}>
            {filteredOptions.map((option) => (
              <div key={option.id}>
                <div
                  className={styles.dropdownItem}
                  onClick={() => handleOptionSelect(option)}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  {option.countryCode && (
                    <img
                      src={`https://flagcdn.com/24x18/${option.countryCode.toLowerCase()}.png`}
                      alt={option.countryCode}
                      className={styles.flag}
                    />
                  )}
                  <div className={styles.itemContent}>
                    <div className={styles.itemTitle}>{option.title}</div>
                    {option.subtitle && (
                      <div className={styles.itemSubtitle}>
                        {option.subtitle}
                      </div>
                    )}
                  </div>
                  {option.airports && option.airports.length > 1 ? (
                    <span className={styles.expandIcon}>›</span>
                  ) : (
                    <span className={styles.airportCode}>
                      {option.airports?.[0]?.code &&
                        `(${option.airports[0].code})`}
                    </span>
                  )}
                </div>
                {option.airports && option.airports.length > 1 && (
                  <div className={styles.airportsList}>
                    {option.airports.map((airport) => (
                      <div
                        key={airport.id}
                        className={styles.airportItem}
                        onClick={() => handleOptionSelect(option, airport)}
                      >
                        <span className={styles.airportName}>
                          {airport.title}
                        </span>
                        {airport.code && (
                          <span className={styles.airportCode}>
                            ({airport.code})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Autocomplete;
